import { Link } from 'react-router-dom'
import { ReaderResponse } from '../models/ReaderResponse'
import { FirmwareResponse } from '../models/FirmwareResponse'
import { extractFirmwaresMap } from 'api/reader-api'
import { ReactElement } from 'react'
import {
  MillisecondsPerSecond,
  SecondsPerDay,
  getDeltaSeconds,
  getOnlyTime,
  getUtcNow,
} from 'utils/utils'

interface Props {
  reader: ReaderResponse
}

const getVersionOrNone = (version?: string): string => {
  return version ? version : 'none'
}

const IsLastFirmwareVersion = (firmware: FirmwareResponse): boolean => {
  return firmware.reader_version === firmware.profile_version
}

const getFirmwareVersion = (
  firmware: FirmwareResponse,
  type: string
): string => {
  if (!firmware) {
    return `${type}: none`
  }

  const readerVersion = getVersionOrNone(firmware.reader_version)
  if (IsLastFirmwareVersion(firmware)) {
    return `${type}: ${readerVersion}`
  }

  const profileVersion = getVersionOrNone(firmware.profile_version)
  return `${type}: ${readerVersion} > ${profileVersion}`
}

const getFirmwareBackgroundColor = (firmware: FirmwareResponse): string => {
  if (!firmware || !firmware.profile_version) {
    return 'bg-[#FFF59D]'
  }

  if (!IsLastFirmwareVersion(firmware)) {
    return 'bg-[#EF9A9A]'
  }

  return 'bg-[#A5D6A7]'
}

const getDeltaLast = (reader: ReaderResponse): string => {
  if (!reader.last_connection_time) {
    return ''
  }

  const delta = getDeltaSeconds(
    getUtcNow(),
    new Date(reader.last_connection_time)
  )
  const days = Math.floor(delta / SecondsPerDay)
  const time = getOnlyTime(new Date(delta * MillisecondsPerSecond))

  return days > 0 ? `${days}.${time}` : time
}

const copyId = (reader: ReaderResponse): void => {
  navigator.clipboard.writeText(reader.id)
}

const ReaderItem = ({ reader }: Props) => {
  const getForEachFirmwares = (): ReactElement[] => {
    let forEachFirmwares: ReactElement[] = []
    extractFirmwaresMap(reader).forEach((firmware, type) => {
      forEachFirmwares.push(
        <span key={type} className={`${getFirmwareBackgroundColor(firmware)}`}>
          {getFirmwareVersion(firmware, type)}
        </span>
      )
    })
    return forEachFirmwares
  }

  return (
    <tr className='odd:bg-white even:bg-gray-50 border'>
      <td className='px-6 py-4 border font-medium text-center text-gray-900 whitespace-nowrap'>
        <Link
          to={`${reader.id}`}
          className='font-medium text-blue-600 hover:underline'
          target='_blank'
        >
          {reader.serial}
        </Link>
      </td>
      <td
        className='px-6 py-4 border font-medium text-center hover:underline hover:cursor-pointer'
        onClick={() => copyId(reader)}
        title='Copy ID to clipboard'
      >
        ID
      </td>
      <td className='px-6 py-4 border'>{reader.note}</td>
      <td className='px-6 py-4 border'>{getDeltaLast(reader)}</td>
      <td className='px-6 py-4 border'>{reader.mqtt_uptime}</td>
      <td className='border'>
        <div className='flex flex-col space-y-1'>{getForEachFirmwares()}</div>
      </td>
      <td className='px-6 py-4 border'>{reader.profile?.name}</td>
      <td className='px-6 py-4 border'>{reader.operator?.name}</td>
    </tr>
  )
}

export default ReaderItem
